/**
 * Reading plan decisions back from the database.
 *
 * The engine's decision strings are stored as text — on plan_records, in a
 * plan version's payload, and inside a saved planning run's full output. When
 * the owner renamed one of them (2026-10-28: "Deferred - Capacity" became
 * "Out - capacity", because "Deferred" belongs to a person's triage choice and
 * the engine reports a condition it found), every row written before that day
 * kept the old text. Nothing rewrites those rows — they are the record of what
 * was said then — so every reader maps the old string to the current one here,
 * and a stored plan reads the same as a fresh run.
 *
 * The table of renamed strings lives in supabase/functions/_shared/storedPlanDecisions.ts,
 * beside the public decision-lineage route that also reads these rows; the app
 * imports that one table. Add a line there when a decision string is renamed;
 * do not edit stored rows and do not compare against an old spelling anywhere else.
 */
import type { PlanningEngineOutput } from './planningEngineModule.types';
import { readStoredPlanDecision } from '../../supabase/functions/_shared/storedPlanDecisions';

export { readStoredPlanDecision };

/**
 * A saved engine output with every decision string in today's words.
 * Returns the same object when nothing in it needed mapping.
 */
export function readStoredEngineOutput(output: PlanningEngineOutput): PlanningEngineOutput {
  if (!output) return output;
  let changed = false;

  const remap = <R extends { planDecision?: string; decisionWithParticipation?: string }>(rows: R[] | undefined): R[] | undefined => {
    if (!rows) return rows;
    return rows.map((row) => {
      const planDecision = readStoredPlanDecision(row.planDecision);
      const withParticipation = readStoredPlanDecision(row.decisionWithParticipation);
      if (planDecision === row.planDecision && withParticipation === row.decisionWithParticipation) return row;
      changed = true;
      return { ...row, planDecision, decisionWithParticipation: withParticipation };
    });
  };

  const planRecords = remap(output.planRecords);
  const priorityPlanRecords = remap(output.priorityPlanRecords);
  const prioritySummaries = output.prioritySummaries?.map((summary) => {
    const withParticipation = readStoredPlanDecision(summary.decisionWithParticipation as string);
    if (withParticipation === summary.decisionWithParticipation) return summary;
    changed = true;
    return { ...summary, decisionWithParticipation: withParticipation as typeof summary.decisionWithParticipation };
  });

  if (!changed) return output;
  return {
    ...output,
    planRecords: planRecords as PlanningEngineOutput['planRecords'],
    priorityPlanRecords: priorityPlanRecords as PlanningEngineOutput['priorityPlanRecords'],
    prioritySummaries: prioritySummaries as PlanningEngineOutput['prioritySummaries'],
  };
}
